import { Component, ElementRef, OnDestroy, viewChild } from '@angular/core';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxFileDropEntry, NgxFileDropModule, FileSystemFileEntry } from 'ngx-file-drop';

import { ButtonComponent } from '../../../../../../shared/_components/button/button.component';
import { InputComponent } from '../../../../../../shared/_components/input/input.component';
import { Subscription } from 'rxjs';

// Components
import { FlowMediaUploadStep } from '../../flows/flow-media-upload/step/flow-media-upload-step.component';

// Models
import { CurrentUploadOperation, InitiateUploadRequest, MediaDetailsUploadFormDataRequest } from '../../../models/media-upload.model';

// Environment
import { environment } from '../../../../../../../environments/environment';

// Icons
import IconLibrary from '../../../../../../shared/_libraries/icon-library.helper';

// The types ODDR signs. The upload API and signing both reject anything else
// (C2PA_MIME_TYPE_SCHEMA), so accepting more here only fails after the user has
// picked the file. Each one must also be on ODDR's C2PA conformance record.
const SUPPORTED_MIME_TYPES: string[] = [
  // Video
  'video/mp4',
  'video/quicktime',

  // Image
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/tiff',
  'image/heif',
  'image/heic',

  // Audio — signed and published to ODDR only; no provider takes audio
  'audio/mpeg',
];

// Per environment: off in production until ODDR's C2PA conformance covers PDF —
// see docs/design/13-pdf-and-audio-signing.md
const PDF_SIGNING_ENABLED = environment.enablePdfSigning;
if (PDF_SIGNING_ENABLED) SUPPORTED_MIME_TYPES.push('application/pdf');

// Largest max file size across all providers (YouTube: 256 GB)
const MAX_FILE_SIZE_BYTES = 274877906944;

// PDFs and audio are published to ODDR only, so the limit is what signing can
// handle: c2pa holds a PDF in memory at ~5x its size and an MP3 at ~2.5x, on a
// 2GB Lambda. See infrastructure/terraform/lambda/c2pa-hashing/main.tf.
const MAX_PDF_SIZE_BYTES = 200 * 1024 * 1024;
const MAX_AUDIO_SIZE_BYTES = 500 * 1024 * 1024;

@Component({
  selector: 'pp-step-media-upload-file-select',
  templateUrl: './step-media-upload-file-select.component.html',
  styleUrl: './step-media-upload-file-select.component.scss',
  imports: [ReactiveFormsModule, NgxFileDropModule, ButtonComponent, InputComponent],
})
export class StepMediaUploadFileSelectComponent extends FlowMediaUploadStep implements OnDestroy {

  // Component handler (!IMPORTANT - Must match step identifier for this flow)
  identifier = 'mediaUploadFlowStepUploadFileSelect';

  readonly thumbnailSection = viewChild.required<ElementRef>('thumbnailSection');

  // File Properties
  fileName = '';
  fileSize = '';
  fileSelected = false;
  fileUploadComplete = false;
  isVideoFile = false;
  override uploadProgress = 0;

  // Form handlers
  fileUploadError!: string | null;
  isProcessingFileUpload = false;
  isProcessingThumbnailUpload = false;
  thumbnailHasBeenUploaded = false;
  thumbnailImageLoaded = false;
  unsupportedThumbnailFile = false;

  // Subscriptions
  override currentUpload$: Subscription;
  uploadError$: Subscription;
  private readonly thumbnailUploadStatus$?: Subscription;
  private readonly uploadProgress$: Subscription;

  // Icons
  public iconLibrary = IconLibrary;

  constructor() {
    super();

    // Define forms
    this.form = new FormGroup({
      file: new FormControl<NgxFileDropEntry | null>(null, { validators: Validators.required }),
      muDetailsTitle: new FormControl<string>('', Validators.required),
    });

    // Subscriptions
    this.currentUpload$ = this.mediaUploadService.currentUpload$.subscribe({
      next: (response: CurrentUploadOperation | null) => {
        if (response?.assetId) {
          this.fileSelected = true;
          this.isVideoFile = response.contentType?.startsWith('video/') ?? false;
          this.cdr.markForCheck();
          if (this.isVideoFile) {
            setTimeout(() => {
              this.thumbnailSection()?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
          }
        }
      },
    });

    this.uploadError$ = this.mediaUploadService.uploadError$.subscribe({
      next: (uploadErrorMessage: string | null) => {
        if (uploadErrorMessage) {
          this.fileUploadError = uploadErrorMessage;
          this.isProcessingFileUpload = false;
          this.cdr.markForCheck();
        }
      },
    });

    // Listen for upload progress
    this.uploadProgress$ = this.mediaUploadService.progressUploadIndicator$.subscribe({
      next: (progress: number) => {
        this.uploadProgress = progress;
        if (progress >= 100) {
          this.fileUploadComplete = true;
        }
        this.cdr.markForCheck();
      },
    });

    // Listen for thumbnail upload status
    this.thumbnailUploadStatus$ = this.mediaUploadService.thumbnailUploadStatus$.subscribe({
      next: () => {
        this.thumbnailHasBeenUploaded = true;
        this.isProcessingThumbnailUpload = false;
        this.cdr.markForCheck();
      },
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.currentUpload$?.unsubscribe();
    this.uploadError$?.unsubscribe();
    this.uploadProgress$?.unsubscribe();
    this.thumbnailUploadStatus$?.unsubscribe();
  }

  get selectedFile(): NgxFileDropEntry | null {
    return this.form?.get('file')?.value;
  }

  get thumbnailImagePath(): string {
    return (this.operation?.thumbnailUrl && this.operationUpdate) ?
      `${this.operation.thumbnailUrl}?cache=${this.operationUpdate.getTime()}` :
      '/assets/images/defaults/default-media-picture.png';
  }

  get supportedFileTypesLabel(): string {
    return `.JPG, .PNG, .GIF, .WEBP, .TIFF, .HEIC, .HEIF, .MOV, .MP4, .MP3${PDF_SIGNING_ENABLED ? ', .PDF' : ''}`;
  }

  get uploadProgressDisplay(): number {
    return Math.round(this.uploadProgress);
  }

  get canProceed(): boolean {
    const titleValid = !!this.form.get('muDetailsTitle')?.value?.trim();
    const fileValid = this.fileSelected;
    const thumbnailValid = this.isVideoFile ? this.thumbnailImageLoaded : true;
    return titleValid && fileValid && thumbnailValid;
  }

  onFileSelected(files: NgxFileDropEntry[]): void {
    this.fileUploadError = null;

    if (files.length === 0) {
      this.form.patchValue({ file: null });
      this.fileUploadError = 'No file was dropped or selected.';
      return;
    }

    if (files.length > 10) {
      this.form.patchValue({ file: null });
      this.fileUploadError = 'A maximum of 10 images can be uploaded at once.';
      return;
    }

    if (files.some(f => f.fileEntry.isDirectory)) {
      this.form.patchValue({ file: null });
      this.fileUploadError = 'Uploading a folder or directory of files is not supported.';
      return;
    }

    // Multi-image batch upload
    if (files.length > 1) {
      this.handleMultiFileUpload(files);
      return;
    }

    // Single file upload (existing flow)
    (files[0].fileEntry as FileSystemFileEntry).file((file: File) => {
      const validationError = this.validateFile(file);
      if (validationError) {
        this.form.patchValue({ file: null });
        this.fileUploadError = validationError;
        return;
      }

      this.isProcessingFileUpload = true;
      this.form.patchValue({ file: files[0] });
      this.fileName = file.name;
      this.fileSize = this.formatFileSize(file.size);

      if (this.form.get('file')?.valid) {
        const fileUploadPayload = {
          fileName: this.selectedFile?.fileEntry.name,
          contentType: this.normaliseMimeType(file.type),
        } as InitiateUploadRequest;
        this.mediaUploadService.initiateUpload(fileUploadPayload, file);
      }
    });
  }

  private handleMultiFileUpload(entries: NgxFileDropEntry[]): void {
    const filePromises = entries.map(
      entry => new Promise<File>(resolve => {
        (entry.fileEntry as FileSystemFileEntry).file(resolve);
      }),
    );

    Promise.all(filePromises).then(resolvedFiles => {
      // Validate all are images
      const nonImages = resolvedFiles.filter(f => !f.type.startsWith('image/'));
      if (nonImages.length > 0) {
        this.fileUploadError = 'Multi-image upload only supports image files. Remove any video files and try again.';
        return;
      }

      // Validate each file
      for (const file of resolvedFiles) {
        const error = this.validateFile(file);
        if (error) {
          this.fileUploadError = `${file.name}: ${error}`;
          return;
        }
      }

      this.isProcessingFileUpload = true;
      this.form.patchValue({ file: entries[0] });
      this.fileName = `${resolvedFiles.length} images`;
      this.fileSize = this.formatFileSize(resolvedFiles.reduce((sum, f) => sum + f.size, 0));

      this.mediaUploadService.initiateBatchUpload(resolvedFiles);
    });
  }

  onThumbnailImageLoaded(): void {
    this.thumbnailImageLoaded = true;
  }

  onThumbnailSelected(files: NgxFileDropEntry[]): void {
    this.thumbnailHasBeenUploaded = false;
    this.thumbnailImageLoaded = false;
    this.unsupportedThumbnailFile = false;

    if (files.length === 0 || files.length > 1 || files[0].fileEntry.isDirectory) {
      this.unsupportedThumbnailFile = true;
      return;
    }

    const allowedFileTypes = ['png', 'jpg', 'jpeg'];
    const fileType = files[0].relativePath.split('.').pop()?.toLowerCase();
    if (!fileType || !allowedFileTypes.includes(fileType)) {
      this.unsupportedThumbnailFile = true;
      return;
    }

    this.isProcessingThumbnailUpload = true;
    (files[0].fileEntry as FileSystemFileEntry).file((file: File) => this.mediaUploadService.compressAndUploadThumbnail(file, true));
  }

  submitAndGoToNextStep(): void {
    if (!this.canProceed) return;

    const payload = {
      title: this.form.get('muDetailsTitle')?.value,
    } as MediaDetailsUploadFormDataRequest;

    this.mediaUploadService.postUploadDetails(payload).subscribe({
      next: () => this.goToNextStep(),
    });
  }

  private validateFile(file: File): string | null {
    if (!file.type || !this.isMimeTypeSupported(this.normaliseMimeType(file.type))) {
      return `File type "${file.type || 'unknown'}" is not supported. Supported file types: ${this.supportedFileTypesLabel}.`;
    }

    const maxSize = this.maxFileSizeFor(this.normaliseMimeType(file.type));
    if (file.size > maxSize) {
      return `File size (${this.formatFileSize(file.size)}) exceeds the maximum allowed (${this.formatFileSize(maxSize)}).`;
    }

    if (file.size === 0) {
      return 'The selected file is empty (0 bytes). Please select a valid file.';
    }

    return null;
  }

  private maxFileSizeFor(mimeType: string): number {
    if (mimeType === 'application/pdf') return MAX_PDF_SIZE_BYTES;
    if (mimeType.startsWith('audio/')) return MAX_AUDIO_SIZE_BYTES;
    return MAX_FILE_SIZE_BYTES;
  }

  /**
   * Some browsers (e.g. Chrome on Windows) report MP3s as audio/mp3; the
   * registered type, and the only one signing accepts, is audio/mpeg.
   * @memberof StepMediaUploadFileSelectComponent
   */
  private normaliseMimeType(mimeType: string): string {
    return mimeType.toLowerCase() === 'audio/mp3' ? 'audio/mpeg' : mimeType;
  }

  private isMimeTypeSupported(mimeType: string): boolean {
    const normalised = mimeType.toLowerCase();
    return SUPPORTED_MIME_TYPES.some(supported => {
      const normalisedSupported = supported.toLowerCase();
      if (normalisedSupported === normalised) return true;
      if (normalisedSupported.endsWith('/*')) {
        return normalised.startsWith(normalisedSupported.slice(0, -1));
      }
      return false;
    });
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }
}
